<script>
  import {
    savedCalculations,
    deleteCalculation,
    duplicateCalculation,
  } from "./stores.js";
  import {
    exportAsCSV,
    exportAsTXT,
    exportAsPDF,
    exportAsJSON,
    copyJSONToClipboard,
    copyAsTXT
  } from "./exportUtils.js";

  export let onNavigate;
  export let onOpenDataModal;
  export let onOpenDaManager;

  let sortOrder = 'newest';
  $: sortedCalculations = [...$savedCalculations].sort(
    (a, b) => sortOrder === 'newest'
      ? new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      : new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
  );

  let activeDropdown = null;
  let activeSubmenu = null;
  let showAllCalculations = false;
  const maxVisibleCards = 8;

  $: visibleCalculations = showAllCalculations 
    ? sortedCalculations 
    : sortedCalculations.slice(0, maxVisibleCards);

  $: hasMoreCards = sortedCalculations.length > maxVisibleCards;

  function toggleDropdown(event, calcId) {
    event.stopPropagation();
    if (activeDropdown === calcId) {
      activeDropdown = null;
      activeSubmenu = null;
    } else {
      activeDropdown = calcId;
      activeSubmenu = null;
    }
  }

  function closeDropdowns() {
    activeDropdown = null;
    activeSubmenu = null;
  }

  function toggleShowMore() {
    showAllCalculations = !showAllCalculations;
  }

  function renameCalc(calc) {
    const newName = prompt("Enter new name:", calc.name);
    if (newName && newName.trim()) {
      calc.name = newName.trim();
      calc.updatedAt = new Date().toISOString();
      savedCalculations.update((all) => all);
    }
  }

  function handleExport(calc, action, pdfOrientation = 'landscape') {
    const totals = calc.tableData.reduce(
      (acc, row) => {
        const daRate = parseFloat(row.daRate) || 0;
        const drawn = parseFloat(row.drawn) || 0;
        const arrears = parseFloat(row.arrears) || 0;
        const daAmount = Math.round(row.basicPay * (daRate / 100));
        const salaryDue = row.basicPay + daAmount;
        const difference = salaryDue - drawn;
        row.daAmount = daAmount;
        row.salaryDue = salaryDue;
        row.difference = difference;
        acc.due += salaryDue;
        acc.drawn += drawn;
        acc.arrears += arrears;
        acc.commuted += row.commuted || 0;
        acc.difference += difference;
        return acc;
      },
      { due: 0, drawn: 0, arrears: 0, commuted: 0, difference: 0 }
    );
    const dueAmount = totals.due - totals.drawn - totals.arrears;
    const shouldHighlightDue = dueAmount > 100;
    switch (action) {
      case 'csv':
        exportAsCSV(calc, totals, dueAmount, shouldHighlightDue);
        break;
      case 'txt':
        exportAsTXT(calc, totals, dueAmount, shouldHighlightDue);
        break;
      case 'pdf-landscape':
        exportAsPDF(calc, totals, dueAmount, shouldHighlightDue, 'landscape');
        break;
      case 'pdf-portrait':
        exportAsPDF(calc, totals, dueAmount, shouldHighlightDue, 'portrait');
        break;
      case 'copy-txt':
        copyAsTXT(calc, totals, dueAmount, shouldHighlightDue);
        break;
      case 'json':
        exportAsJSON(calc);
        break;
      case 'copy-json':
        copyJSONToClipboard(calc);
        break;
    }
    closeDropdowns();
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  function getCalculationSummary(calc) {
    if (!calc.tableData || calc.tableData.length === 0) {
      return { totalDue: 0, totalDrawn: 0, totalArrears: 0, months: 0 };
    }

    const summary = calc.tableData.reduce((acc, row) => {
      acc.totalDue += (row.salaryDue || 0);
      acc.totalDrawn += (row.drawn || 0);
      acc.totalArrears += (row.arrears || 0);
      return acc;
    }, { totalDue: 0, totalDrawn: 0, totalArrears: 0 });

    summary.months = calc.tableData.length;
    return summary;
  }

  function handleCardClick(calcId) {
    if (activeDropdown) {
      return; // Don't navigate if dropdown is open
    }
    onNavigate("calculator", calcId);
  }

  function handleCardKeydown(event, calcId) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick(calcId);
    }
  }
</script>

<svelte:window on:click={closeDropdowns} />

<section id="homeView" class="view active">
  <div class="app-header">
    <h1>Salary Breakdown Pro</h1>
    <p class="slogan">Generate your official salary, DA & arrears statement in seconds</p>
  </div>

  <!-- New Calculation Card -->
  <button
    class="new-calculation-card glass-card"
    on:click={() => onNavigate("calculator", null)}
    on:keydown={(e) => e.key === 'Enter' && onNavigate("calculator", null)}
  >
    <div class="new-card-content">
      <div class="new-card-icon">+</div>
      <div class="new-card-text">
        <h2>New Calculation</h2>
        <p>Create a new salary or pension calculation</p>
      </div>
    </div>
  </button>

  <!-- Saved Calculations -->
  <div class="glass-card saved-calculations-section">
    <div class="section-header">
      <h3>Saved Calculations</h3>
      <span class="count-badge">{sortedCalculations.length}</span>
      <div class="sort-bar" style="margin-bottom:0; margin-left: 16px;">
        <label for="sortOrder">Sort by:</label>
        <select id="sortOrder" bind:value={sortOrder}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>
    </div>
    
    {#if sortedCalculations.length === 0}
      <div class="empty-state">
        <div class="empty-icon">📊</div>
        <p>No saved calculations yet</p>
        <p class="empty-subtitle">Create your first calculation to get started</p>
      </div>
    {:else}
      <div class="calculations-grid">
        {#each visibleCalculations as calc (calc.id)}
          {@const summary = calc.tableData ? calc.tableData.length : 0}
          <div class="calculation-card-wrapper">
            <div 
              class="calculation-card glass-card"
              on:click={() => handleCardClick(calc.id)}
              on:keydown={(e) => handleCardKeydown(e, calc.id)}
              role="button"
              tabindex="0"
              aria-label="Open calculation: {calc.name}"
            >
              <div class="card-header">
                <div class="card-title">
                  <h4>{calc.name}</h4>
                  <span class="card-type-badge {calc.salaryType}">
                    {calc.salaryType === 'employee' ? 'Employee' : 'Pensioner'}
                  </span>
                </div>
              </div>
              
              <div class="card-preview">
                <div class="preview-row">
                  <span class="preview-label">Period:</span>
                  <span class="preview-value">{calc.startDate} to {calc.endDate}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">Basic Pay:</span>
                  <span class="preview-value">₹{calc.basicPay}</span>
                </div>
                <div class="preview-row">
                  <span class="preview-label">Months:</span>
                  <span class="preview-value">{summary}</span>
                </div>
              </div>
              
              <div class="card-summary">
                <div class="summary-item">
                  <span class="summary-label">Total Due</span>
                  <span class="summary-value due">₹{calc.tableData ? calc.tableData.reduce((a, r) => a + (r.salaryDue || 0), 0).toFixed(2) : '0.00'}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total Drawn</span>
                  <span class="summary-value drawn">₹{calc.tableData ? calc.tableData.reduce((a, r) => a + (r.drawn || 0), 0).toFixed(2) : '0.00'}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Difference</span>
                  <span class="summary-value difference {calc.tableData && calc.tableData.reduce((a, r) => a + (r.salaryDue || 0), 0) - calc.tableData.reduce((a, r) => a + (r.drawn || 0), 0) > 0 ? 'positive' : 'negative'}">
                    ₹{calc.tableData ? (calc.tableData.reduce((a, r) => a + (r.salaryDue || 0), 0) - calc.tableData.reduce((a, r) => a + (r.drawn || 0), 0)).toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>
              
              <div class="card-footer">
                <span class="last-updated">
                  Updated {new Date(calc.updatedAt).toLocaleString()}
                </span>
              </div>
            </div>
            
            <!-- Floating 3-dot menu button -->
            <button 
              class="floating-menu-trigger"
              on:click={(e) => toggleDropdown(e, calc.id)}
              on:keydown={(e) => e.key === 'Enter' && toggleDropdown(e, calc.id)}
              title="More options"
              aria-label="More options for {calc.name}"
              tabindex="0"
            >
              ⋯
            </button>
            
            {#if activeDropdown === calc.id}
              <div class="floating-dropdown-menu solid-bg" on:click|stopPropagation>
                <button on:click={() => renameCalc(calc)}>Rename</button>
                <button on:click={() => duplicateCalculation(calc.id)}>Duplicate</button>
                <button on:click={() => activeSubmenu = activeSubmenu === 'export' ? null : 'export'}>Export ▸</button>
                <button on:click={() => activeSubmenu = activeSubmenu === 'json' ? null : 'json'}>Export Card Data ▸</button>
                <hr />
                <button class="danger" on:click={() => {
                  if (confirm("Are you sure you want to delete this calculation?")) {
                    deleteCalculation(calc.id);
                  }
                }}>Delete</button>
                {#if activeSubmenu === 'export'}
                  <div class="submenu solid-bg">
                    <button on:click={() => handleExport(calc, 'csv')}>Export as CSV</button>
                    <button on:click={() => handleExport(calc, 'txt')}>Export as TXT</button>
                    <button on:click={() => handleExport(calc, 'pdf-landscape')}>Export as PDF (Landscape)</button>
                    <button on:click={() => handleExport(calc, 'pdf-portrait')}>Export as PDF (Portrait)</button>
                    <button on:click={() => handleExport(calc, 'copy-txt')}>Copy as TXT</button>
                  </div>
                {/if}
                {#if activeSubmenu === 'json'}
                  <div class="submenu solid-bg">
                    <button on:click={() => handleExport(calc, 'json')}>Export as JSON</button>
                    <button on:click={() => handleExport(calc, 'copy-json')}>Copy as JSON</button>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/each}
      </div>

      {#if hasMoreCards}
        <div class="show-more-container">
          <button 
            class="show-more-btn"
            on:click={toggleShowMore}
            on:keydown={(e) => e.key === 'Enter' && toggleShowMore()}
          >
            {showAllCalculations ? 'Show Less' : `Show ${sortedCalculations.length - maxVisibleCards} More`}
          </button>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Settings Section -->
  <div class="glass-card settings-section">
    <h3>Settings & Data Management</h3>
    <div class="settings-grid">
      <button class="settings-card" on:click={onOpenDataModal}>
        <div class="settings-icon">💾</div>
        <div class="settings-content">
          <h4>Manage Calculations</h4>
          <p>Import, export, or clear all calculations</p>
        </div>
      </button>
      <button class="settings-card" on:click={onOpenDaManager}>
        <div class="settings-icon">🗂️</div>
        <div class="settings-content">
          <h4>DA Manager</h4>
          <p>Visualize, edit, and manage all DA lists</p>
        </div>
      </button>
    </div>
  </div>
</section>

<style>
  .sort-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }
  .sort-bar label {
    font-weight: 500;
    color: var(--text-color);
  }
  .sort-bar select {
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid var(--glass-border);
    background: var(--glass-bg);
    color: var(--text-color);
    font-size: 1rem;
  }
  .submenu {
    position: absolute;
    left: 100%;
    top: 0;
    min-width: 180px;
    z-index: 2000;
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    border-radius: 8px;
    border: 1px solid var(--glass-border);
    background: var(--glass-bg);
    padding: 8px 0;
    margin-left: 2px;
  }
  .solid-bg {
    background: var(--glass-bg) !important;
    border: 1px solid var(--glass-border);
    color: var(--text-color);
  }
  .new-calculation-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px dashed var(--glass-border);
    background: linear-gradient(135deg, var(--glass-bg), rgba(255, 255, 255, 0.05));
    width: 100%;
    text-align: left;
    padding: 0;
    font-family: inherit;
    color: inherit;
  }

  .new-calculation-card:hover {
    border-color: var(--primary-color);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .new-calculation-card:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .new-card-content {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 30px;
  }

  .new-card-icon {
    font-size: 3rem;
    color: var(--primary-color);
    font-weight: bold;
  }

  .new-card-text h2 {
    margin: 0 0 8px 0;
    color: var(--text-color);
  }

  .new-card-text p {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.9rem;
  }

  .saved-calculations-section {
    margin-top: 30px;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .count-badge {
    background: var(--primary-color);
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: bold;
  }

  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--text-muted);
  }

  .empty-icon {
    font-size: 4rem;
    margin-bottom: 20px;
  }

  .empty-subtitle {
    font-size: 0.9rem;
    margin-top: 8px;
  }

  .calculations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
  }

  .calculation-card-wrapper {
    position: relative;
  }

  .calculation-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid var(--glass-border);
    position: relative;
    text-align: left;
    background: var(--glass-bg);
    border-radius: 12px;
    padding: 20px;
  }

  .calculation-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
    border-color: var(--primary-color);
  }

  .calculation-card:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 15px;
  }

  .card-title {
    flex: 1;
  }

  .card-title h4 {
    margin: 0 0 8px 0;
    font-size: 1.1rem;
    color: var(--text-color);
  }

  .card-type-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .card-type-badge.employee {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  }

  .card-type-badge.pensioner {
    background: rgba(59, 130, 246, 0.2);
    color: #3b82f6;
  }

  .card-preview {
    margin-bottom: 15px;
  }

  .preview-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 0.85rem;
  }

  .preview-label {
    color: var(--text-muted);
  }

  .preview-value {
    color: var(--text-color);
    font-weight: 500;
  }

  .card-summary {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 15px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
  }

  .summary-item {
    text-align: center;
  }

  .summary-label {
    display: block;
    font-size: 0.7rem;
    color: var(--text-muted);
    margin-bottom: 4px;
  }

  .summary-value {
    display: block;
    font-size: 0.85rem;
    font-weight: bold;
  }

  .summary-value.positive {
    color: #22c55e;
  }

  .summary-value.negative {
    color: #ef4444;
  }

  .card-footer {
    border-top: 1px solid var(--glass-border);
    padding-top: 12px;
  }

  .last-updated {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .settings-section {
    margin-top: 30px;
  }

  .settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-top: 20px;
  }

  .settings-card {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 20px;
    background: none;
    border: 1px solid var(--glass-border);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
  }

  .settings-card:hover {
    border-color: var(--primary-color);
    transform: translateY(-2px);
  }

  .settings-icon {
    font-size: 1.5rem;
  }

  .settings-content h4 {
    margin: 0 0 4px 0;
    color: var(--text-color);
  }

  .settings-content p {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-muted);
  }

  .show-more-container {
    display: flex;
    justify-content: center;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid var(--glass-border);
  }

  .show-more-btn {
    background: var(--primary-color);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .show-more-btn:hover {
    background: var(--primary-color-dark, #0056b3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .show-more-btn:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    .calculations-grid {
      grid-template-columns: 1fr;
    }
    
    .new-card-content {
      flex-direction: column;
      text-align: center;
      gap: 15px;
    }
    
    .card-summary {
      grid-template-columns: 1fr;
      gap: 8px;
    }
  }

  .floating-menu-trigger {
    position: absolute;
    top: 10px;
    right: 10px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    color: var(--text-muted);
    font-size: 1.2rem;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    transition: all 0.2s ease;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .floating-menu-trigger:hover {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-color);
    border-color: var(--primary-color);
    transform: scale(1.05);
  }

  .floating-menu-trigger:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .floating-dropdown-menu {
    position: absolute;
    top: 45px;
    right: 10px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: 8px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 180px;
    padding: 8px 0;
  }

  .floating-dropdown-menu button {
    display: block;
    width: 100%;
    padding: 8px 16px;
    background: none;
    border: none;
    text-align: left;
    color: var(--text-color);
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .floating-dropdown-menu button:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .floating-dropdown-menu hr {
    margin: 8px 0;
    border: none;
    border-top: 1px solid var(--glass-border);
  }

  .floating-dropdown-menu button.danger {
    color: #ef4444;
  }

  .floating-dropdown-menu button.danger:hover {
    background: rgba(239, 68, 68, 0.1);
  }

  .app-header {
    text-align: center;
    margin-bottom: 2rem;
  }

  .app-header h1 {
    font-size: 2.5rem;
    color: var(--text-color);
    margin-bottom: 0.5rem;
  }

  .slogan {
    font-size: 1.1rem;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
  }
</style>
